/** Seconds of no speed increase that counts as a plateau. */
const PLATEAU_WINDOW = 1.0;
/** m/s minimum increase needed to reset the plateau timer. */
const PLATEAU_THRESHOLD = 0.1;
/** Hard-stop timeout in seconds. */
const MAX_DURATION = 12.0;

export interface DragMetrics {
  state: "idle" | "running" | "done";
  elapsed: number;
  maxSpeedReached: number;
  timeTo50: number | null;        // seconds to reach 50% of refTopSpeed
  timeTo80: number | null;        // seconds to reach 80% of refTopSpeed
  accelerationTime: number | null; // elapsed when plateau/timeout triggered
  distanceTravelled: number;       // metres since throttle first pressed
}

/**
 * Measures drag-strip acceleration for one vehicle.
 *
 * Usage per frame:
 *   tracker.tick(snap.speed, input.throttle, dt)
 *   const m = tracker.getMetrics()
 *
 * Call reset() before each run.
 */
export class PerformanceTracker {
  private state: "idle" | "running" | "done" = "idle";
  private elapsed = 0;
  private maxSpeedReached = 0;
  private timeTo50: number | null = null;
  private timeTo80: number | null = null;
  private accelerationTime: number | null = null;
  private distanceTravelled = 0;
  private plateauTimer = 0;
  private lastPeakSpeed = 0;

  constructor(
    /** Theoretical top speed from computeStats — used to compute 50%/80% thresholds. */
    private readonly refTopSpeed: number,
  ) {}

  reset(): void {
    this.state = "idle";
    this.elapsed = 0;
    this.maxSpeedReached = 0;
    this.timeTo50 = null;
    this.timeTo80 = null;
    this.accelerationTime = null;
    this.distanceTravelled = 0;
    this.plateauTimer = 0;
    this.lastPeakSpeed = 0;
  }

  tick(speed: number, throttle: number, dt: number): void {
    if (this.state === "done") return;

    const spd = Math.abs(speed);

    if (this.state === "idle") {
      if (throttle > 0) {
        this.state = "running";
        this.lastPeakSpeed = spd;
      } else {
        return;
      }
    }

    this.elapsed += dt;
    this.distanceTravelled += spd * dt;

    if (spd > this.maxSpeedReached) this.maxSpeedReached = spd;

    if (this.timeTo50 === null && spd >= this.refTopSpeed * 0.5) {
      this.timeTo50 = this.elapsed;
    }
    if (this.timeTo80 === null && spd >= this.refTopSpeed * 0.8) {
      this.timeTo80 = this.elapsed;
    }

    if (spd > this.lastPeakSpeed + PLATEAU_THRESHOLD) {
      this.lastPeakSpeed = spd;
      this.plateauTimer = 0;
    } else {
      this.plateauTimer += dt;
    }

    if (this.plateauTimer >= PLATEAU_WINDOW || this.elapsed >= MAX_DURATION) {
      this.accelerationTime = this.elapsed - this.plateauTimer; // exclude flat tail
      this.state = "done";
    }
  }

  getMetrics(): DragMetrics {
    return {
      state: this.state,
      elapsed: this.elapsed,
      maxSpeedReached: this.maxSpeedReached,
      timeTo50: this.timeTo50,
      timeTo80: this.timeTo80,
      accelerationTime: this.accelerationTime,
      distanceTravelled: this.distanceTravelled,
    };
  }
}
